/**
 * BuzzBit X — Connect ChatGPT documentation.
 *
 * Public page at /docs/chatgpt. ChatGPT-only setup (Plugins / Connectors,
 * OAuth, verify, revoke). Claude Code docs stay on /docs/claude and /docs/mcp.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { MCP_ENDPOINT } from '@/config/mcpConnect';
import { t } from '@/i18n';
import { CodeBlock, Section } from './claudeDocsParts/components';

const SECTIONS = [
  { id: 'overview', labelKey: 'docs.chatgpt.overview.title' as const },
  { id: 'find', labelKey: 'docs.chatgpt.find.title' as const },
  { id: 'developer-mode', labelKey: 'docs.chatgpt.devMode.title' as const },
  { id: 'add', labelKey: 'docs.chatgpt.add.title' as const },
  { id: 'oauth', labelKey: 'docs.chatgpt.oauth.title' as const },
  { id: 'verify', labelKey: 'docs.chatgpt.verify.title' as const },
  { id: 'disconnect', labelKey: 'docs.chatgpt.disconnect.title' as const },
];

const ChatGPTDocs: React.FC = () => (
  <div className="min-h-screen bg-gray-50 pb-cookie-banner [--pb-cookie-base:0px]">
    <header className="border-b border-gray-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link to="/" className="inline-flex min-w-0 items-center">
          <BrandLogo size="sm" />
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/docs/chatgpt" className="font-medium text-gray-900">
            {t('docs.chatgpt.nav')}
          </Link>
          <Link
            to="/settings/integrations"
            className="rounded-lg bg-gray-900 px-3 py-1.5 text-white hover:bg-gray-800"
          >
            {t('docs.chatgpt.openSettings')}
          </Link>
        </nav>
      </div>
    </header>

    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 py-8 md:grid-cols-[220px_1fr]">
      <aside className="md:sticky md:top-8 md:self-start">
        <h3 className="mb-3 text-xs uppercase tracking-wide text-gray-500">{t('docs.chatgpt.nav')}</h3>
        <nav className="space-y-1">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="block rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              {t(s.labelKey)}
            </a>
          ))}
        </nav>
      </aside>

      <main className="min-w-0">
        <h1 className="mb-2 break-words text-3xl font-semibold text-gray-900">{t('docs.chatgpt.title')}</h1>
        <p className="mb-10 text-gray-600">{t('docs.chatgpt.intro')}</p>

        <Section id="overview" title={t('docs.chatgpt.overview.title')}>
          <p>{t('docs.chatgpt.overview.body')}</p>
          <p className="text-sm font-medium text-gray-900">{t('docs.chatgpt.endpointLabel')}</p>
          <CodeBlock>{MCP_ENDPOINT}</CodeBlock>
        </Section>

        <Section id="find" title={t('docs.chatgpt.find.title')}>
          <p>{t('docs.chatgpt.find.plugins')}</p>
          <p>{t('docs.chatgpt.find.connectors')}</p>
        </Section>

        <Section id="developer-mode" title={t('docs.chatgpt.devMode.title')}>
          <p>{t('docs.chatgpt.devMode.body')}</p>
        </Section>

        <Section id="add" title={t('docs.chatgpt.add.title')}>
          <p>{t('docs.chatgpt.add.body')}</p>
          <CodeBlock>{MCP_ENDPOINT}</CodeBlock>
        </Section>

        <Section id="oauth" title={t('docs.chatgpt.oauth.title')}>
          <p>{t('docs.chatgpt.oauth.body')}</p>
        </Section>

        <Section id="verify" title={t('docs.chatgpt.verify.title')}>
          <p>{t('docs.chatgpt.verify.body')}</p>
          <p>
            <Link to="/settings/connected-apps" className="text-gray-900 underline">
              {t('connectedApps.link')}
            </Link>
          </p>
        </Section>

        <Section id="disconnect" title={t('docs.chatgpt.disconnect.title')}>
          <p>{t('docs.chatgpt.disconnect.body')}</p>
        </Section>

        <footer className="mt-16 border-t border-gray-200 pt-6 text-xs text-gray-500">
          {t('docs.chatgpt.helpPrefix')}{' '}
          <a href={`mailto:${t('docs.chatgpt.supportEmail')}`} className="text-gray-900 underline">
            {t('docs.chatgpt.supportEmail')}
          </a>
        </footer>
      </main>
    </div>
  </div>
);

export default ChatGPTDocs;
